// Libraries
import React, { useState } from 'react';

// Components
import WarningModal from '@/views/components/modals/WarningModal';

// Types
import type { DefaultModalProps } from '@/views/components/modals/DefaultModal';
import Comparison from '@/types/comparison';

// Utils
import { formatMessage } from '@/utils/i18n';
import {
  comparisonHasAllUnallocatedOrMissingItems,
  comparisonHasAllUnallocatedPortfolios,
  comparisonHasPlanRemovedPortfolioItem,
  comparisonHasUnallocatedPortfolioItem,
  comparisonIsMissingAllItems,
  isPlanRemovedPortfolioModification,
  isUnallocatedPortfolioModification,
} from '@/utils/comparison';
import ModalBody from './components/ModalBody';

interface Props extends DefaultModalProps {
  comparison: Comparison;
  onConfirm: () => Promise<void>;
}

const ComparisonMissingItemsModal = ({ onConfirm, comparison, ...rest }: Props) => {
  const isMissingAllItems = comparisonIsMissingAllItems(comparison);
  const isAllMissingOrUnallocatedPortfolios = comparisonHasAllUnallocatedOrMissingItems(comparison);
  const hasUnallocatedPortfolios = comparisonHasUnallocatedPortfolioItem(comparison);
  const hasPlanRemovedPortfolios = comparisonHasPlanRemovedPortfolioItem(comparison);
  const allItemsAreUnallocatedPortfolios = comparisonHasAllUnallocatedPortfolios(comparison);

  const unallocatedPortfolios = comparison.modifiedItems?.filter(comparisonItems => isUnallocatedPortfolioModification(comparisonItems)) || [];
  const removedPlanPortfolios = comparison.modifiedItems?.filter(comparisonItems => isPlanRemovedPortfolioModification(comparisonItems)) || [];
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onRerun = async () => {
    setIsSubmitting(true);
    await onConfirm();
  };

  const hidePrimaryButton = isMissingAllItems || allItemsAreUnallocatedPortfolios || isAllMissingOrUnallocatedPortfolios;

  return (
    <WarningModal
      title={formatMessage('ComparisonMissingItemsModal.Title')}
      body={
        <ModalBody
          comparison={comparison}
          isMissingAllItems={isMissingAllItems}
          hasUnallocatedPortfolios={hasUnallocatedPortfolios}
          hasPlanRemovedPortfolios={hasPlanRemovedPortfolios}
          isAllMissingOrUnallocatedPortfolios={isAllMissingOrUnallocatedPortfolios}
          unallocatedPortfolios={unallocatedPortfolios}
          removedPlanPortfolios={removedPlanPortfolios}
          allItemsAreUnallocatedPortfolios={allItemsAreUnallocatedPortfolios}
        />
      }
      confirmLabel={formatMessage('ComparisonMissingItemsModal.Action')}
      className="comparison-missing-items-modal"
      onConfirm={onRerun}
      hidePrimaryButton={hidePrimaryButton}
      isSubmitting={isSubmitting}
      {...rest}
    />
  );
};

export default ComparisonMissingItemsModal;
